// # Settings page component
import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import GeneralSettings from './settings_tabs/GeneralSettings';
import SecuritySettings from './settings_tabs/SecuritySettings';
import SubscriptionSettings from './settings_tabs/SubscriptionSettings';
import HelpSettings from './settings_tabs/HelpSettings';
import PrivacySettings from './settings_tabs/privacy';
import BillingSettings from './settings_tabs/billing';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import cacheManager from '../../auth/cache_manager';
import './settings.css';

const Settings = () => {
  const { isAdmin, user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      if (user && user.uid) {
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  const getBasePath = () => isAdmin ? '/admin/settings' : '/settings';

  const navigationIcons = [
    { 
      id: 'general', 
      icon: 'fas fa-user-cog',
      label: 'General',
      component: GeneralSettings 
    },
    { 
      id: 'security', 
      icon: 'fas fa-shield-alt',
      label: 'Security',
      component: SecuritySettings 
    },
    { 
      id: 'privacy', 
      icon: 'fas fa-lock',
      label: 'Privacy',
      component: PrivacySettings 
    },
    { 
      id: 'billing', 
      icon: 'fas fa-credit-card',
      label: 'Billing',
      component: BillingSettings 
    },
    { 
      id: 'subscriptions', 
      icon: 'fas fa-receipt',
      label: 'Subscriptions',
      component: SubscriptionSettings 
    },
    { 
      id: 'help', 
      icon: 'fas fa-question-circle',
      label: 'Help',
      component: HelpSettings 
    }
  ];

  return (
    <div className={`stng_mn_settings-page ${isDarkMode ? 'stng_mn_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <main className="stng_mn_settings-content">
        <div className="stng_mn_settings-container">
          <h1>
            <i className={`fas fa-cog ${isAdmin ? 'stng_mn_admin-icon' : ''}`}></i>
            <span>{isAdmin ? 'Admin Settings' : 'Settings'}</span>
          </h1>
          
          <div className="stng_mn_settings-layout">
            {/* # Vertical settings navigation */}
            <div className="stng_mn_settings-sidebar">
              {navigationIcons.map((item) => (
                <NavLink
                  key={item.id}
                  to={`${getBasePath()}/${item.id}`}
                  className={({ isActive }) => 
                    `stng_mn_sidebar-link ${isActive ? 'active' : ''}`
                  }
                >
                  <div className="stng_mn_sidebar-item">
                    <i className={item.icon}></i>
                    <span>{item.label}</span>
                  </div>
                </NavLink>
              ))}
            </div>

            {/* # Settings content area */}
            <div className="stng_mn_settings-tab-content">
              <Routes>
                <Route index element={<Navigate to="general" replace />} />
                {navigationIcons.map((item) => (
                  <Route
                    key={item.id}
                    path={item.id}
                    element={<item.component isDarkMode={isDarkMode} />}
                  />
                ))}
                <Route path="*" element={<Navigate to="general" replace />} />
              </Routes>
            </div>
          </div>
        </div>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default Settings;
